/**
 * Parse schedule/task names into user-friendly display format
 *
 * Input formats:
 * - "cargurus-ford-fl-orlando-20260119022538" → "Orlando, FL - Ford"
 * - "autotrader-stellantis-mi-detroit-20260118153022" → "Detroit, MI - Stellantis"
 * - "ford-inventory-collection" → "Ford Inventory Collection"
 */

// OEM display names mapping
const OEM_DISPLAY_NAMES: Record<string, string> = {
  ford: 'Ford',
  stellantis: 'Stellantis',
  hyundai: 'Hyundai',
  gm: 'GM',
  toyota: 'Toyota',
  honda: 'Honda',
  nissan: 'Nissan',
  bmw: 'BMW',
  mercedes: 'Mercedes',
  volkswagen: 'Volkswagen',
  kia: 'Kia',
  subaru: 'Subaru',
  mazda: 'Mazda',
  chevrolet: 'Chevrolet',
  chrysler: 'Chrysler',
  dodge: 'Dodge',
  jeep: 'Jeep',
  ram: 'RAM'
};

// US State abbreviations
const STATE_ABBREVIATIONS: Record<string, string> = {
  al: 'AL',
  ak: 'AK',
  az: 'AZ',
  ar: 'AR',
  ca: 'CA',
  co: 'CO',
  ct: 'CT',
  de: 'DE',
  fl: 'FL',
  ga: 'GA',
  hi: 'HI',
  id: 'ID',
  il: 'IL',
  in: 'IN',
  ia: 'IA',
  ks: 'KS',
  ky: 'KY',
  la: 'LA',
  me: 'ME',
  md: 'MD',
  ma: 'MA',
  mi: 'MI',
  mn: 'MN',
  ms: 'MS',
  mo: 'MO',
  mt: 'MT',
  ne: 'NE',
  nv: 'NV',
  nh: 'NH',
  nj: 'NJ',
  nm: 'NM',
  ny: 'NY',
  nc: 'NC',
  nd: 'ND',
  oh: 'OH',
  ok: 'OK',
  or: 'OR',
  pa: 'PA',
  ri: 'RI',
  sc: 'SC',
  sd: 'SD',
  tn: 'TN',
  tx: 'TX',
  ut: 'UT',
  vt: 'VT',
  va: 'VA',
  wa: 'WA',
  wv: 'WV',
  wi: 'WI',
  wy: 'WY',
  dc: 'DC'
};

// Data sources to strip
const DATA_SOURCES = new Set(['cargurus', 'autotrader', 'cars', 'carfax', 'edmunds', 'truecar']);

/** Capitalize first letter of each word */
function titleCase(str: string): string {
  return str
    .split(/[-_\s]+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Check if string is a timestamp (all digits, 8+ chars) */
function isTimestamp(str: string): boolean {
  return /^\d{8,}$/.test(str);
}

/** Filter meaningful parts from schedule name */
function filterMeaningfulParts(parts: string[]): string[] {
  return parts.filter(p => !DATA_SOURCES.has(p) && !isTimestamp(p));
}

/** Extract OEM, state, and city from parts */
function extractComponents(parts: string[]): {
  oem: string | null;
  state: string | null;
  city: string | null;
} {
  let oem: string | null = null;
  let state: string | null = null;
  const cityParts: string[] = [];

  for (const part of parts) {
    if (!oem && OEM_DISPLAY_NAMES[part]) {
      oem = OEM_DISPLAY_NAMES[part];
      continue;
    }
    if (!state && STATE_ABBREVIATIONS[part]) {
      state = STATE_ABBREVIATIONS[part];
      continue;
    }
    // Collect remaining parts as potential city name components
    if (part.length > 0) {
      cityParts.push(part);
    }
  }

  // Join city parts and title case (e.g., "ft myers" → "Ft Myers")
  const city = cityParts.length > 0 ? titleCase(cityParts.join(' ')) : null;

  return { oem, state, city };
}

/** Build display name from components */
function buildDisplayName(
  oem: string | null,
  state: string | null,
  city: string | null
): string | null {
  if (city && state && oem) return `${city}, ${state} - ${oem}`;
  if (city && state) return `${city}, ${state}`;
  if (city && oem) return `${city} - ${oem}`;
  if (oem) return `${oem} Collection`;
  return null;
}

/**
 * Parse a schedule name into a user-friendly display format
 */
export function parseScheduleName(name: string): string {
  if (!name) return 'Unnamed Schedule';

  const parts = name.toLowerCase().split('-');
  const meaningfulParts = filterMeaningfulParts(parts);
  const { oem, state, city } = extractComponents(meaningfulParts);
  const displayName = buildDisplayName(oem, state, city);

  if (displayName) return displayName;

  // Fallback: title case the original (minus timestamp)
  const cleanedParts = parts.filter(p => !isTimestamp(p));
  return cleanedParts.length > 0 ? titleCase(cleanedParts.join(' ')) : titleCase(name);
}

/**
 * Parse a task name to extract a cleaner display version
 */
export function parseTaskName(taskName: string): string {
  if (!taskName) return 'Unknown Task';

  const lowerName = taskName.toLowerCase();
  for (const [key, displayName] of Object.entries(OEM_DISPLAY_NAMES)) {
    if (lowerName.includes(key)) {
      const afterOem = lowerName.split(key)[1] || '';
      const cleaned = afterOem
        .split('-')
        .filter(p => p.length > 0)
        .map(titleCase)
        .join(' ');
      return cleaned ? `${displayName} ${cleaned}` : displayName;
    }
  }

  return titleCase(taskName.replace(/-/g, ' '));
}

/** Get short display name (just location) */
export function getShortScheduleName(name: string): string {
  const parsed = parseScheduleName(name);
  const dashIndex = parsed.indexOf(' - ');
  return dashIndex > 0 ? parsed.substring(0, dashIndex) : parsed;
}

/** Extract OEM from schedule or task name */
export function extractOem(name: string): string | null {
  if (!name) return null;
  const lowerName = name.toLowerCase();
  for (const [key, displayName] of Object.entries(OEM_DISPLAY_NAMES)) {
    if (lowerName.includes(key)) return displayName;
  }
  return null;
}
